import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, Settings, ShieldCheck, UserPlus, Send, Mic, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { baaApi } from '../baa/baaApi';

export const OPEN_WELCOME_EVENT = 'practcom:open-welcome-checklist';

const STEPS = [
  { key: 'settings', icon: Settings, title: 'Complete Practice Settings first',
    text: 'Add your logo, office phone, address, your dentists and your Google review link. Fill in whatever you can now; you can finish the rest later.',
    cta: 'Open Practice Settings', to: '/practice-settings' },
  { key: 'baa', icon: ShieldCheck, title: 'Sign the HIPAA Business Associate Agreement',
    text: 'It takes a minute and is in Practice Settings. Texts and emails to patients stay paused until it is signed.',
    cta: 'Sign it now', to: '/practice-settings#baa' },
  { key: 'patient', icon: UserPlus, title: 'Add your first patient',
    text: 'Tip: use yourself as the test patient. Enter your own name, cell phone and email so you see exactly what your patients will get.',
    cta: 'Add a patient', to: '/add-patient' },
  { key: 'postop', icon: Send, title: 'Send your first post-op instructions',
    text: 'Pick your test patient, choose a procedure and send the instructions by text and email. Check your phone and open them like a patient would.',
    cta: 'Send instructions', to: '/assign-procedure' },
  { key: 'dictate', icon: Mic, title: 'Try Dictate Visit',
    text: 'Just talk through a visit and PractCom prepares the instructions, Med Cards and follow-ups for you to review and send. Step-by-step help is in Tutorials.',
    cta: 'Open Tutorials', event: 'practcom:open-tutorials' },
];

export const WelcomeChecklist = () => {
  const { user, hideWelcomeChecklist } = useAuth();
  const navigate = useNavigate();
  const storeKey = `practcom_welcome_steps_${user?.id}`;
  const [manual, setManual] = useState(false);
  const [done, setDone] = useState(() => JSON.parse(localStorage.getItem(storeKey) || '{}'));
  const open = !!user?.showWelcomeChecklist || manual;

  useEffect(() => {
    const show = () => setManual(true);
    window.addEventListener(OPEN_WELCOME_EVENT, show);
    return () => window.removeEventListener(OPEN_WELCOME_EVENT, show);
  }, []);

  useEffect(() => {
    if (!open) return;
    window.dispatchEvent(new Event('practcom:baa-hide'));
    baaApi.status().then((s) => s.signed && setDone((d) => ({ ...d, baa: true }))).catch(() => {});
  }, [open]);

  useEffect(() => { localStorage.setItem(storeKey, JSON.stringify(done)); }, [done, storeKey]);

  if (!open) return null;
  const close = () => { setManual(false); hideWelcomeChecklist(); };
  const go = (s) => {
    setDone((d) => ({ ...d, [s.key]: true }));
    close();
    if (s.event) { navigate('/'); setTimeout(() => window.dispatchEvent(new Event(s.event)), 300); return; }
    const [path, hash] = s.to.split('#');
    navigate(path);
    if (hash) setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' }), 1200);
  };
  const count = STEPS.filter((s) => done[s.key]).length;

  return (
    <div className="fixed inset-0 z-[9000] bg-slate-900/60 flex items-center justify-center p-3 sm:p-6" data-testid="welcome-checklist-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92dvh] overflow-y-auto">
        <div className="relative bg-[#0b2545] text-white px-6 pt-6 pb-5 rounded-t-2xl">
          <button onClick={close} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10" aria-label="Close" data-testid="welcome-checklist-close-btn"><X className="w-5 h-5" /></button>
          <h2 className="text-2xl font-bold pr-8">Welcome to PractCom{user?.firstName ? `, ${user.firstName}` : ''}!</h2>
          <p className="text-sky-100 text-sm mt-2 leading-relaxed">
            You'll be sending your first patient instructions in about 10 minutes. Follow these five steps in order.
            You can reopen this list any time from <strong>Getting Started</strong> in the menu.
          </p>
          <p className="text-xs text-sky-200 mt-3" data-testid="welcome-checklist-progress">{count} of {STEPS.length} done</p>
        </div>
        <ol className="p-4 sm:p-6 space-y-3">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isDone = !!done[s.key];
            return (
              <li key={s.key} className={`flex gap-3 p-4 rounded-xl border ${isDone ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200'}`} data-testid={`welcome-step-${s.key}`}>
                <button onClick={() => setDone((d) => ({ ...d, [s.key]: !isDone }))} aria-label={isDone ? 'Mark not done' : 'Mark done'} className="shrink-0 mt-0.5" data-testid={`welcome-step-${s.key}-toggle`}>
                  {isDone ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <Circle className="w-6 h-6 text-slate-300" />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 flex items-center gap-2"><Icon className="w-4 h-4 text-sky-700 shrink-0" />{i + 1}. {s.title}</p>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{s.text}</p>
                  <button onClick={() => go(s)} className="mt-2 text-sm font-semibold text-sky-800 hover:underline" data-testid={`welcome-step-${s.key}-go`}>{s.cta} →</button>
                </div>
              </li>
            );
          })}
        </ol>
        <div className="px-6 pb-6">
          <button onClick={close} className="w-full h-11 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold" data-testid="welcome-checklist-start-btn">Let's get started</button>
          <p className="text-center text-xs text-slate-500 mt-3">Questions? Ask Dr. G (bottom right) or email admin@practcom.com</p>
        </div>
      </div>
    </div>
  );
};
